import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CalendarDays, MapPin, Sparkles, Ticket, Download, QrCode } from 'lucide-react';
import api from '@/utils/api';
import ProgressionHero from '@/components/progression/ProgressionHero';

const STATUS_COLORS = {
  draft: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  submitted: 'bg-blue-50 text-blue-800 border-blue-200',
  pending_payment: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  confirmed: 'bg-success-50 text-success-800 border-success-200',
  waitlisted: 'bg-secondary-50 text-secondary-800 border-secondary-200',
  cancelled: 'bg-red-50 text-red-800 border-red-200',
  checked_in: 'bg-primary-50 text-primary-800 border-primary-200',
};

const MyEventsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ticketOpen, setTicketOpen] = useState(null); // { registration, event }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/me/events');
        setItems(data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-700" /></div>;

  return (
    <div className="p-8 max-w-4xl">
      <h1 className="font-display text-3xl font-bold text-primary-900 mb-1">My events</h1>
      <p className="text-neutral-600 mb-6">Everything you've RSVP'd to.</p>

      {/* F2.c Progression hero - rank, level, points, recent activity */}
      <div className="mb-8">
        <ProgressionHero />
      </div>

      {items.length === 0 ? (
        <Card className="border shadow-soft">
          <CardContent className="p-12 text-center">
            <Sparkles className="h-8 w-8 text-secondary-500 mx-auto mb-4" />
            <h3 className="font-display text-xl font-semibold text-primary-900 mb-2">No RSVPs yet</h3>
            <p className="text-neutral-600 mb-4">Browse upcoming Dostination gatherings and reserve your spot.</p>
            <Button asChild><Link to="/events">Explore events</Link></Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map(({ registration, event, resume_url }) => {
            const paymentStatus = registration.payment_status;
            const needsPaymentCompletion = (
              paymentStatus === 'pending_payment'
              || paymentStatus === 'instructions_viewed'
            ) && registration.status !== 'confirmed';
            return (
            <Card key={registration.id} className="border shadow-soft" data-testid={`my-events-row-${registration.id}`}>
              <CardContent className="p-5 flex flex-col md:flex-row gap-4 md:items-center">
                <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-primary-600 to-primary-900 flex items-center justify-center text-white shadow-soft flex-shrink-0">
                  <Ticket className="h-8 w-8" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-display text-xl font-semibold text-primary-900">{event.title || 'Event'}</h3>
                    <Badge variant="outline" className={`capitalize ${STATUS_COLORS[registration.status] || STATUS_COLORS.draft}`}>
                      {(registration.status || 'draft').replace(/_/g, ' ')}
                    </Badge>
                    {needsPaymentCompletion && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300" data-testid={`payment-pending-badge-${registration.id}`}>
                        Payment pending
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm text-neutral-600">
                    {event.start_datetime && (
                      <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-secondary-700" />{new Date(event.start_datetime).toLocaleDateString()}</span>
                    )}
                    {(event.city || event.location?.city) && (
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-secondary-700" />{event.city || event.location.city}</span>
                    )}
                    {registration.table_assignment && (
                      <span className="flex items-center gap-1 font-semibold text-secondary-800" data-testid={`table-assignment-${registration.id}`}>
                        <Ticket className="h-3.5 w-3.5 text-secondary-700" /> Table {registration.table_assignment}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {needsPaymentCompletion && resume_url && (
                    <a
                      href={resume_url}
                      className="inline-flex items-center gap-1 rounded-lg bg-amber-500 hover:bg-amber-600 text-amber-950 font-semibold text-sm px-3.5 py-2 shadow-soft"
                      role="link"
                      aria-label={`Complete payment for ${event.title || 'event'}`}
                      data-testid={`complete-payment-btn-${registration.id}`}
                    >
                      Complete payment
                    </a>
                  )}
                  {registration.status === 'confirmed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTicketOpen({ registration, event })}
                      data-testid={`ticket-view-btn-${registration.id}`}
                    >
                      <QrCode className="h-3.5 w-3.5 mr-1.5" /> Ticket
                    </Button>
                  )}
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/events/${event.slug}`}>View event</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!ticketOpen} onOpenChange={(o) => !o && setTicketOpen(null)}>
        <DialogContent className="max-w-md">
          {ticketOpen && (
            <>
              <DialogHeader>
                <DialogTitle>{ticketOpen.event.title || 'Your ticket'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                {ticketOpen.registration.ticket_code && (
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ticket code</div>
                    <div className="font-mono text-2xl font-bold text-secondary-600 tracking-wider" data-testid="ticket-modal-code">
                      {ticketOpen.registration.ticket_code}
                    </div>
                  </div>
                )}
                <div className="flex justify-center">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${ticketOpen.registration.id}/qr.png`}
                    alt="QR ticket"
                    className="w-64 h-64 rounded-lg border border-neutral-100"
                    data-testid="ticket-modal-qr"
                  />
                </div>
                <div className="text-center text-xs text-neutral-500">
                  Present this QR at the venue for check-in.
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${ticketOpen.registration.id}/ticket.pdf`}
                  className="inline-flex items-center justify-center w-full gap-2 py-2.5 rounded-lg bg-primary-800 hover:bg-primary-900 text-white text-sm font-semibold"
                  data-testid="ticket-modal-pdf-btn"
                >
                  <Download className="h-4 w-4" /> Download PDF
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyEventsPage;
